import React, { useEffect, useRef } from "react";
import { FaChevronDown, FaChevronUp } from "react-icons/fa";
import SimparyChat from "../wizard/SimparyChat";
import FeedMatchSection from "./FeedMatchSection";
import { buildSearchContextSummary } from "../../utils/searchContextSummary";
import styles from "../../styles/pages/feed/FeedPage.module.css";

export const MATCH_SHORTLIST_EXHAUSTED_PROMPT =
  "None of these fit? Say what was off and we'll search again.";

export const FeedMatchesChat = ({
  chatExpanded,
  onChatExpandedChange,
  onMatchResults,
  chatCardClassName = "",
  collapsedBarClassName = "",
  panelVariant = "default",
  hasResults = false,
  searchParams = null,
  collapsedTitleClassName = "",
  collapsedHintClassName = "",
  collapsedIconClassName = "",
  collapseButtonClassName = "",
  showCollapsedContextSummary = true,
  coachPrompt = null,
  onCoachPromptConsumed = null,
}) => {
  const contextSummary = buildSearchContextSummary(searchParams);
  const titleClassName = collapsedTitleClassName || styles.chatCollapsedTitle;
  const hintClassName = collapsedHintClassName || styles.chatCollapsedHint;
  const iconClassName = collapsedIconClassName || styles.chatCollapsedIcon;
  const prevHasResultsRef = useRef(hasResults);

  const collapseBtnClassName =
    collapseButtonClassName || styles.chatCollapseBtn;

  // Auto-collapse when a new shortlist appears (including rematch after loading).
  useEffect(() => {
    const hadResults = prevHasResultsRef.current;
    prevHasResultsRef.current = hasResults;
    if (hasResults && !hadResults) {
      onChatExpandedChange(false);
    }
  }, [hasResults, onChatExpandedChange]);

  // Keep SimparyChat mounted while collapsed so the transcript isn't wiped.
  // Use inline display:none — hero/card CSS `display: flex` can win over [hidden].
  return (
    <>
      <div
        className={chatCardClassName || styles.matchesChatCard}
        style={chatExpanded ? undefined : { display: "none" }}
        aria-hidden={!chatExpanded}
      >
        <button
          type="button"
          className={collapseBtnClassName}
          onClick={() => onChatExpandedChange(false)}
          aria-label="Collapse chat"
          aria-expanded={chatExpanded}
          tabIndex={chatExpanded ? 0 : -1}
        >
          <FaChevronUp className={iconClassName} aria-hidden />
        </button>
        <SimparyChat
          embedded
          panel
          panelVariant={panelVariant}
          onMatchResults={onMatchResults}
          coachPrompt={coachPrompt}
          onCoachPromptConsumed={onCoachPromptConsumed}
          // Only refine when a shortlist is on screen. Otherwise a stale
          // prior (e.g. old GPU match) can poison a brand-new category ask.
          priorSearchParams={hasResults ? searchParams : null}
        />
      </div>
      {!chatExpanded && (
        <button
          type="button"
          className={collapsedBarClassName || styles.chatCollapsedBar}
          onClick={() => onChatExpandedChange(true)}
          aria-expanded={false}
        >
          <span className={titleClassName}>
            {hasResults ? "Refine your search" : "Search the catalog"}
          </span>
          <span className={hintClassName}>
            {hasResults
              ? showCollapsedContextSummary && contextSummary
                ? `Based on: ${contextSummary}`
                : "Tap to adjust your search"
              : "Tap to search"}
          </span>
          <FaChevronDown className={iconClassName} aria-hidden />
        </button>
      )}
    </>
  );
};
const FeedMatchesTab = ({
  active,
  products,
  matchLoading,
  searchParams,
  matchScoreRevision,
  activeDecision,
  className = "",
  emptyHintClassName = "",
  variant = "default",
  mode = "full",
  showMarketplace = true,
  skipFeaturedProduct = false,
  sectionTitle,
  onShortlistExhausted = null,
  loadError = null,
  onRetryLoad = null,
}) => {
  if (!active) return null;

  return (
    <div className={`${styles.matchResultsContainer} ${className}`.trim()}>
      <FeedMatchSection
        products={products}
        loading={matchLoading}
        searchParams={searchParams}
        matchScoreRevision={matchScoreRevision}
        activeDecision={activeDecision}
        emptyHintClassName={emptyHintClassName}
        variant={variant}
        mode={mode}
        showMarketplace={showMarketplace}
        skipFeaturedProduct={skipFeaturedProduct}
        sectionTitle={sectionTitle}
        onShortlistExhausted={onShortlistExhausted}
        loadError={loadError}
        onRetryLoad={onRetryLoad}
      />
    </div>
  );
};
export default React.memo(FeedMatchesTab);
